import { type } from 'arktype';
import { isDayRangeWithin } from '../lib/day-key-math';

export const ABSENCE_SUBJECT_TYPE = 'timetracking.absence';

export type AbsenceOperation = 'create' | 'update' | 'remove';

export const ABSENCE_PORTIONS = ['full', 'fraction', 'hours', 'range'] as const;

export type AbsencePortion = (typeof ABSENCE_PORTIONS)[number];

export const DEFAULT_BOOKING_PORTIONS: readonly AbsencePortion[] = ['full', 'range'];

export const absencePortionKindSchema = type.enumerated(...ABSENCE_PORTIONS);

export const ABSENCE_RANGE_MAX_DAYS = 366;

export function absenceRangeError(range: { dateFrom: string; dateTo: string }): string | null {
  return isDayRangeWithin(range.dateFrom, range.dateTo, ABSENCE_RANGE_MAX_DAYS)
    ? null
    : `an ordered date range of at most ${ABSENCE_RANGE_MAX_DAYS} days`;
}

export interface AbsenceAmountInput {
  portion: AbsencePortion;
  fractionValue?: number;
  durationMinutes?: number;
  startTime?: string;
  endTime?: string;
}

export interface AbsencePayload {
  operation: AbsenceOperation;
  absenceId?: string;
  /** Subject of the absence — NOT the requester (`requestedByPersonId` is the actor, who may differ). */
  personId: string;
  /** Resolves to the `slot_type_definitions` row keyed `absence:<slug>`. */
  absenceTypeSlug: string;
  from: string;
  /** ISO date, inclusive. */
  to: string;
  portion: AbsencePortion;
  fractionValue?: number;
  durationMinutes?: number;
  startTime?: string;
  endTime?: string;
  reason?: string;
  /** Storage file ids. */
  attachments?: string[];
}

export const absencePayloadSchema = type({
  operation: "'create' | 'update' | 'remove'",
  'absenceId?': 'string',
  personId: 'string',
  absenceTypeSlug: 'string',
  from: 'string',
  to: 'string',
  portion: "'full' | 'fraction' | 'hours' | 'range'",
  'fractionValue?': 'number',
  'durationMinutes?': 'number',
  'startTime?': 'string',
  'endTime?': 'string',
  'reason?': 'string',
  'attachments?': 'string[]',
})
  .narrow((payload, ctx) => {
    if (payload.operation === 'create') return payload.absenceId === undefined || ctx.mustBe('a create payload without absenceId');
    return payload.absenceId !== undefined || ctx.mustBe('an update/remove payload with absenceId');
  })
  .narrow((payload, ctx) => {
    const hasFraction = payload.fractionValue !== undefined;
    const hasHours = payload.durationMinutes !== undefined;
    const hasStart = payload.startTime !== undefined;
    const hasEnd = payload.endTime !== undefined;
    if (payload.portion === 'full')
      return (!hasFraction && !hasHours && !hasStart && !hasEnd) || ctx.mustBe('a full portion without value fields');
    if (payload.portion === 'fraction')
      return (hasFraction && !hasHours && !hasStart && !hasEnd) || ctx.mustBe('a fraction portion with fractionValue only');
    if (payload.portion === 'hours')
      return (hasHours && !hasFraction && !hasStart && !hasEnd) || ctx.mustBe('an hours portion with durationMinutes only');
    return (hasStart && hasEnd && !hasFraction && !hasHours) || ctx.mustBe('a range portion with startTime and endTime only');
  })
  .onUndeclaredKey('reject');
